import './styles/IconRenderer.scss';

import { CircleNotch } from '@phosphor-icons/react';
import { motion } from 'motion/react';
import { HTMLAttributes } from 'react';

import { useClassHelper } from '../../hooks';
import { appTypes } from '../../types/common';

const variantTypes = {
  spinner: 'spinner',
  'spinner-light': 'spinner-light',
  success: 'success',
  error: 'error',
  '5g': '5g',
  apple: 'apple',
  android: 'android',
} as const;

interface Props extends HTMLAttributes<HTMLDivElement> {
  variant: keyof typeof variantTypes;
  app?: keyof typeof appTypes;

  size?: string; // Icon size (e.g.: "w-8 h-8", "w-12 h-12")
  color?: string; // Icon color (e.g.: "text-blue-500", "text-red-600")
  strokeWidth?: string; // Stroke width for SVG
  customClassName?: string; // Additional Tailwind classes
}

const SpinnerClassName = 'c-SpinnerIcon';

export const IconRenderer = ({
  variant,
  app = appTypes.portal,
  size = '', // Default size
  color = 'text-current', // Default color
  strokeWidth,
  customClassName = '',
  className,
  ...rest
}: Props) => {
  const isWebsheetApp = app === appTypes.websheet;
  const c = useClassHelper(SpinnerClassName);

  const tailwindClasses = `${size} ${color} ${customClassName}`.trim();
  const combinedClassName = `${className || ''} ${tailwindClasses}`.trim();

  if (variant === variantTypes['spinner-light']) {
    return (
      <div
        {...rest}
        className={`${SpinnerClassName} ${c(
          '-spinner-light',
        )} ${combinedClassName}`}
      >
        <motion.span
          className={tailwindClasses}
          animate={{ rotate: 360 }}
          transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear',
          }}
        >
          <CircleNotch
            aria-label="loading spinner icon"
            className={tailwindClasses}
          />
        </motion.span>
      </div>
    );
  }

  if (variant === variantTypes.spinner) {
    const lines = [
      { x1: '128', y1: '32', x2: '128', y2: '64' },
      { x1: '195.88', y1: '60.12', x2: '173.25', y2: '82.75' },
      { x1: '224', y1: '128', x2: '192', y2: '128' },
      { x1: '195.88', y1: '195.88', x2: '173.25', y2: '173.25' },
      { x1: '128', y1: '224', x2: '128', y2: '192' },
      { x1: '60.12', y1: '195.88', x2: '82.75', y2: '173.25' },
      { x1: '32', y1: '128', x2: '64', y2: '128' },
      { x1: '60.12', y1: '60.12', x2: '82.75', y2: '82.75' },
    ];

    return (
      <div
        {...rest}
        className={`${SpinnerClassName} ${c(
          isWebsheetApp ? '--spinner-websheet' : '--spinner',
        )} ${combinedClassName}`}
      >
        <motion.span
          animate={{ rotate: -360 }}
          transition={{
            repeat: Infinity,
            duration: 3.5,
            ease: 'linear',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 256 256"
            aria-label="loading spinner icon"
            className={tailwindClasses}
          >
            <rect width="256" height="256" fill="none" />
            {lines.map((line, index) => {
              const opacity = 1.1 - (index + 1) * (1 / lines.length);

              return (
                <line
                  key={index}
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={strokeWidth || '16'}
                  style={{ opacity }}
                />
              );
            })}
          </svg>
        </motion.span>
      </div>
    );
  }

  if (variant === variantTypes.success) {
    return (
      <div
        {...rest}
        className={`${SpinnerClassName} ${c(
          isWebsheetApp ? '--success-websheet' : '--success',
        )} ${combinedClassName}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 256 256"
          aria-label="success icon"
          className={tailwindClasses}
        >
          <rect width="256" height="256" fill="none" />
          <motion.polyline
            points="88 136 112 160 168 104"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={strokeWidth || '16'}
            initial={{ display: 'none', pathLength: 0 }}
            animate={{ display: 'block', pathLength: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.5,
            }}
          />
          <motion.circle
            cx="128"
            cy="128"
            r="96"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={strokeWidth || '16'}
            initial={{ display: 'none', pathLength: 0 }}
            animate={{ display: 'block', pathLength: 1 }}
            transition={{
              duration: 0.5,
            }}
          />
        </svg>
      </div>
    );
  }

  if (variant === variantTypes.error) {
    return (
      <div
        {...rest}
        className={`${SpinnerClassName} ${c(
          isWebsheetApp ? '--error-websheet' : '--error',
        )} ${combinedClassName}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 256 256"
          aria-label="error icon"
          className={tailwindClasses}
        >
          <rect width="256" height="256" fill="none" />
          <motion.circle
            cx="128"
            cy="128"
            r="96"
            fill="none"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeWidth={strokeWidth || '16'}
            initial={{ display: 'none', pathLength: 0 }}
            animate={{ display: 'block', pathLength: 1 }}
            transition={{
              duration: 0.5,
            }}
          />
          <motion.line
            x1="128"
            y1="136"
            x2="128"
            y2="80"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={strokeWidth || '16'}
            initial={{ display: 'none', pathLength: 0 }}
            animate={{ display: 'block', pathLength: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.8,
            }}
          />
          <motion.circle
            cx="128"
            cy="172"
            r="12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="circle"
            transition={{
              duration: 0.25,
              delay: 0.5,
            }}
          />
        </svg>
      </div>
    );
  }

  if (variant === variantTypes.apple) {
    return (
      <div {...rest} className={combinedClassName}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={tailwindClasses}
        >
          <path
            d="M13.6168 6.00037C13.5313 6.06637 12.0219 6.91272 12.0219 8.79465C12.0219 10.9714 13.9425 11.7415 14 11.7605C13.9912 11.8075 13.6949 12.8152 12.9873 13.842C12.3565 14.7455 11.6976 15.6476 10.6952 15.6476C9.69291 15.6476 9.43496 15.0682 8.27785 15.0682C7.15023 15.0682 6.74929 15.6667 5.83245 15.6667C4.91561 15.6667 4.27589 14.8306 3.54035 13.8038C2.68837 12.5981 2 10.725 2 8.9472C2 6.09571 3.86316 4.58343 5.69684 4.58343C6.67117 4.58343 7.48336 5.22002 8.09507 5.22002C8.67731 5.22002 9.58531 4.54529 10.6938 4.54529C11.1139 4.54529 12.6233 4.58343 13.6168 6.00037ZM10.1675 3.3381C10.626 2.79685 10.9503 2.04584 10.9503 1.29483C10.9503 1.19069 10.9414 1.08508 10.9222 1C10.1764 1.02787 9.28903 1.49432 8.75396 2.11184C8.33387 2.58709 7.94178 3.3381 7.94178 4.09938C7.94178 4.21379 7.96094 4.3282 7.96978 4.36487C8.01695 4.37367 8.0936 4.38394 8.17025 4.38394C8.83946 4.38394 9.68112 3.93803 10.1675 3.3381Z"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  }

  if (variant === variantTypes.android) {
    return (
      <div {...rest} className={combinedClassName}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={tailwindClasses}
        >
          <path
            d="M5.66873 9.85791C6.15782 9.85791 6.55432 10.2544 6.55432 10.7435V14.5424C6.55432 15.0315 6.15782 15.428 5.66873 15.428C5.1797 15.428 4.7832 15.0315 4.7832 14.5424V10.7435C4.7832 10.2545 5.1797 9.85797 5.66873 9.85797V9.85791Z"
            fill="currentColor"
          />
          <path
            d="M3.64707 5.29053C3.64415 5.32411 3.6416 5.35806 3.6416 5.39239V11.1481C3.6416 11.7671 4.13156 12.2658 4.73981 12.2658H10.593C11.2013 12.2658 11.6913 11.7671 11.6913 11.1481V5.39239C11.6913 5.35806 11.69 5.32398 11.6872 5.29053H3.64707Z"
            fill="currentColor"
          />
          <path
            d="M9.66449 9.85791C10.1536 9.85791 10.5501 10.2544 10.5501 10.7435V14.5424C10.5501 15.0315 10.1536 15.428 9.66449 15.428C9.17546 15.428 8.77896 15.0315 8.77896 14.5424V10.7435C8.77896 10.2545 9.17546 9.85797 9.66449 9.85797V9.85791ZM2.21909 5.80276C2.70812 5.80276 3.10462 6.19926 3.10462 6.68835V10.4873C3.10462 10.9764 2.70812 11.3729 2.21909 11.3729C1.72999 11.3729 1.3335 10.9764 1.3335 10.4874V6.68841C1.33343 6.19932 1.72993 5.80276 2.21909 5.80276ZM13.1146 5.80276C13.6037 5.80276 14.0002 6.19926 14.0002 6.68835V10.4873C14.0002 10.9764 13.6037 11.3729 13.1146 11.3729C12.6255 11.3729 12.229 10.9764 12.229 10.4874V6.68841C12.229 6.19932 12.6255 5.80276 13.1146 5.80276ZM3.67331 4.91244C3.69793 3.1445 5.24101 1.69494 7.22176 1.50391H8.11177C10.0926 1.695 11.6356 3.14462 11.6602 4.91244H3.67331Z"
            fill="currentColor"
          />
          <path
            d="M3.98096 1.3335L4.91512 2.95158M11.352 1.3335L10.4178 2.95158"
            stroke="currentColor"
            strokeWidth="0.279837"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.47686 3.32813C6.47761 3.57526 6.27308 3.77618 6.01998 3.77686C5.76701 3.77749 5.56129 3.57774 5.56055 3.33062V3.32813C5.55986 3.08094 5.76439 2.88008 6.01743 2.8794C6.2704 2.87871 6.47611 3.07839 6.47686 3.32564V3.32813ZM9.89684 3.32813C9.89759 3.57526 9.69306 3.77618 9.43996 3.77686C9.18699 3.77749 8.98128 3.57774 8.98053 3.33062V3.32813C8.97985 3.08094 9.18437 2.88008 9.43741 2.8794C9.69038 2.87871 9.89609 3.07839 9.89684 3.32564V3.32813Z"
            fill="white"
          />
        </svg>
      </div>
    );
  }

  return null;
};
